import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Signup page — Depth 0 (outside Shell).
 * Renders at /signup. Redirects to command-centre on success.
 * No Inter font. Uses system-ui per style guide.
 */

function getPasswordStrength(pw: string): 'weak' | 'fair' | 'strong' {
  if (pw.length < 12) return 'weak';
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  const score = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;
  if (score >= 4 && pw.length >= 16) return 'strong';
  if (score >= 3) return 'fair';
  return 'weak';
}

const STRENGTH_COLORS: Record<string, string> = {
  weak: '#dc2626',
  fair: 'var(--accent-amber)',
  strong: 'var(--accent-teal)',
};

export const SignupPage: React.FC<{
  onNavigate: (view: string) => void;
}> = ({ onNavigate }) => {
  const { signup, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      onNavigate('command-centre');
    }
  }, [isAuthenticated, onNavigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const success = await signup(email, password, companyName);
    if (success) {
      onNavigate('command-centre');
    }
  };

  const isRateLimited = error?.code === 'rate_limited';
  const isAccountExists = error?.code === 'account_exists';
  const formDisabled = isLoading || isRateLimited;
  const strength = password ? getPasswordStrength(password) : null;

  const fieldError = (field: string): string | undefined => {
    if (error?.details) {
      const match = error.details.find((d) => d.field === field);
      return match?.message;
    }
    return undefined;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg-primary)', fontFamily: 'system-ui, sans-serif' }}
    >
      <div
        className="w-full max-w-sm rounded-lg p-6"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-default)',
        }}
      >
        <h1
          className="text-xl font-semibold mb-6 text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          Create your company
        </h1>

        {error && !error.details && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-4 rounded-md p-3 text-sm"
            style={{
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#fca5a5',
            }}
          >
            {isAccountExists ? (
              <>
                An account with this email already exists.{' '}
                <button
                  onClick={() => onNavigate(`login?email=${encodeURIComponent(email)}`)}
                  className="underline"
                  style={{ color: 'var(--accent-teal)' }}
                >
                  Log in instead?
                </button>
              </>
            ) : isRateLimited ? (
              'Too many signup attempts. Please wait before trying again.'
            ) : (
              error.message
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label
              htmlFor="signup-email"
              className="block text-sm mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={formDisabled}
              placeholder="founder@company.com"
              required
              className="w-full rounded-md px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: fieldError('email')
                  ? '1px solid #dc2626'
                  : '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              aria-describedby={fieldError('email') ? 'signup-email-error' : undefined}
            />
            {fieldError('email') && (
              <p id="signup-email-error" className="mt-1 text-xs" style={{ color: '#fca5a5' }}>
                {fieldError('email')}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="signup-password"
              className="block text-sm mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Password
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={formDisabled}
                placeholder="12+ characters, mixed case + digit"
                required
                className="w-full rounded-md px-3 py-2 pr-10 text-sm outline-none"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: fieldError('password')
                    ? '1px solid #dc2626'
                    : '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                }}
                aria-describedby={
                  fieldError('password') ? 'signup-password-error' : 'signup-password-strength'
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                style={{ color: 'var(--text-tertiary)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
            {fieldError('password') && (
              <p id="signup-password-error" className="mt-1 text-xs" style={{ color: '#fca5a5' }}>
                {fieldError('password')}
              </p>
            )}
            {strength && !fieldError('password') && (
              <p
                id="signup-password-strength"
                className="mt-1 text-xs"
                style={{ color: STRENGTH_COLORS[strength] }}
              >
                Strength: {strength}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="signup-company"
              className="block text-sm mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Company name
            </label>
            <input
              id="signup-company"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={formDisabled}
              placeholder="Your company name"
              required
              maxLength={100}
              className="w-full rounded-md px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: fieldError('company_name')
                  ? '1px solid #dc2626'
                  : '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              aria-describedby={fieldError('company_name') ? 'signup-company-error' : undefined}
            />
            {fieldError('company_name') && (
              <p id="signup-company-error" className="mt-1 text-xs" style={{ color: '#fca5a5' }}>
                {fieldError('company_name')}
              </p>
            )}
            <p className="mt-1 text-xs text-right" style={{ color: 'var(--text-tertiary)' }}>
              {companyName.length}/100
            </p>
          </div>

          <button
            type="submit"
            disabled={formDisabled || !email || !password || !companyName}
            className="w-full rounded-md py-2.5 text-sm font-medium transition-opacity disabled:opacity-50"
            style={{
              backgroundColor: 'var(--accent-amber)',
              color: '#0E1420',
            }}
          >
            {isLoading ? 'Creating your company...' : 'Create Company'}
          </button>
        </form>

        <p
          className="mt-4 text-center text-sm"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Already have an account?{' '}
          <button
            onClick={() => onNavigate('login')}
            className="underline"
            style={{ color: 'var(--accent-teal)' }}
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};
